import { Fragment as _Fragment } from "react/jsx-runtime";
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import FormattedMessage from 'I18n/components/FormattedMessage';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { NavMarker } from 'react-rhumb';
import { Flow } from 'signup-constants/Flow';
import { useSignupEmbedder } from 'signup-embedder/hooks/useSignupEmbedder';
import styled from 'styled-components';
import { H1 } from 'UIComponents/elements/headings';
import { AnimatedStepBody } from '../animation/StepBodyAnimations';
import { ExperimentKeys } from '../experiments/ExperimentKeys';
import { TrackWhen, useExperiment } from '../experiments/useExperiment';
import { useIsFramedCentredStep } from '../hooks/useIsFramedCentredStep';
import { OAuthSignupSection } from '../oauth/OAuthSignupSection';
import { useResponsiveness } from '../responsiveness/ResponsivenessProvider';
import { getRouteParams } from '../routing/getRouteParams';
import { isSoftwareSignup } from '../store/flow/selectors';
import { devices } from '../styles/viewports.styles';
import { getIntegrationName } from '../utils/getIntegrationName';
import NextButtonWrapper from '../views-components/NextButtonWrapper';
import { OptionalFormattedMessage } from '../views-components/OptionalFormattedMessage';
import PrivacyBanner from '../views-components/PrivacyBanner';
import { Step } from '../views/Step';
import { getLandingPageButton, getLandingPageTitle } from './getLandingPageText';
import { HaveAnAccountLink } from './HaveAnAccountLink';
import { useLandingPage } from './useLandingPage';
import { UserInfoForm } from './UserInfoForm';
export const StyledH1 = styled(H1).withConfig({
  displayName: "LandingPageView__StyledH1",
  componentId: "sc-176kln1-0"
})(["margin-top:40px;font-size:2.25rem;", " ", " @media ", "{", "}@media ", "{margin-top:", ";}"], props => props.flowKey === Flow.Connect && `font-size: 1.75rem;`, props => props.theme.embedded && ` margin-top: 0;
      font-size: 1.75rem;`, devices.tablet, props => props.isFramedCentredStep && ` margin-top: 0;`, devices.desktop, props => props.isFramedCentredStep ? '20px' : '0');
const EmbeddedHaveAnAccount = styled.div.withConfig({
  displayName: "LandingPageView__EmbeddedHaveAnAccount",
  componentId: "sc-176kln1-1"
})(["", ""], props => props.theme.embedded && `margin-top: 1.5rem;
font-size: 14px;
margin-bottom: 1.5rem;`);
export const LandingPageSubtitle = styled.div.withConfig({
  displayName: "LandingPageView__LandingPageSubtitle",
  componentId: "sc-176kln1-2"
})(["", ""], props => props.theme.embedded && `margin-bottom: 1rem;`);
export const LandingPageTitleWrapper = styled.div.withConfig({
  displayName: "LandingPageView__LandingPageTitleWrapper",
  componentId: "sc-176kln1-3"
})(["", ""], props => props.shouldSetMaxWidth && `max-width: 305px;`);

const LandingPageView = () => {
  const stepKey = Step.LandingPage;
  const {
    flowKey
  } = getRouteParams();
  const {
    chooseEmailSignUp
  } = useLandingPage(stepKey);
  const {
    isActive: isInstanceOfSignupEmbedder
  } = useSignupEmbedder();
  const {
    isMobile
  } = useResponsiveness();
  const {
    isFramedCentredStep
  } = useIsFramedCentredStep();
  useExperiment(ExperimentKeys.ACQ123, TrackWhen.enrolled);
  useExperiment(ExperimentKeys.ACQ124, TrackWhen.enrolled);
  useExperiment(ExperimentKeys.ACQ127, TrackWhen.enrolled);
  const {
    isAssignedToVariant: isAssignedToVariant125
  } = useExperiment(ExperimentKeys.ACQ125, TrackWhen.enrolled);

  const renderTitle = () => {
    const {
      title,
      titleLabel,
      subtitleLabel
    } = getLandingPageTitle(flowKey, isAssignedToVariant125());
    const defaultMobileSubtitle = `views.${stepKey}.mobileSubtitle.default`;
    const subtitle = isMobile && isSoftwareSignup() ? defaultMobileSubtitle : subtitleLabel;
    return /*#__PURE__*/_jsxs(LandingPageTitleWrapper, {
      className: `text-center m-x-auto flex-column justify-center`,
      shouldSetMaxWidth: !isValidI18nKey(titleLabel),
      children: [/*#__PURE__*/_jsx(StyledH1, {
        flowKey: flowKey,
        isFramedCentredStep: isFramedCentredStep,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: title,
          options: {
            integratorName: getIntegrationName()
          }
        })
      }), /*#__PURE__*/_jsx(LandingPageSubtitle, {
        children: /*#__PURE__*/_jsx(OptionalFormattedMessage, {
          message: subtitle
        })
      })]
    });
  };

  const renderUserInfo = isFramedCentredStepAligned => {
    return /*#__PURE__*/_jsx(UserInfoForm, {
      className: isFramedCentredStepAligned ? '' : 'm-top-6'
    });
  };

  const renderContent = isFramedCentredStepAligned => {
    return /*#__PURE__*/_jsxs(_Fragment, {
      children: [/*#__PURE__*/_jsx(OAuthSignupSection, {}), /*#__PURE__*/_jsxs("form", {
        action: "blue-button-in-all-fields-ios",
        onSubmit: chooseEmailSignUp,
        children: [renderUserInfo(isFramedCentredStepAligned), /*#__PURE__*/_jsx(NextButtonWrapper, {
          className: isFramedCentredStep ? '' : 'm-top-3',
          "data-selenium": "EMAIL_button",
          label: getLandingPageButton(stepKey),
          onClick: chooseEmailSignUp
        })]
      })]
    });
  };

  const renderFooter = () => {
    return isInstanceOfSignupEmbedder ? /*#__PURE__*/_jsx(EmbeddedHaveAnAccount, {
      className: "text-center",
      children: /*#__PURE__*/_jsx(HaveAnAccountLink, {})
    }) : /*#__PURE__*/_jsx(PrivacyBanner, {});
  };

  return /*#__PURE__*/_jsx(NavMarker, {
    name: stepKey,
    children: /*#__PURE__*/_jsx(AnimatedStepBody, {
      animationDelay: true,
      children: /*#__PURE__*/_jsxs("div", {
        "data-selenium": `${stepKey}_VIEW`,
        children: [renderTitle(), renderContent(isFramedCentredStep), renderFooter()]
      })
    })
  });
};

export default LandingPageView;